import React, { useEffect, useState } from 'react';

interface AnimatedNumberProps {
  value: number | string;
  duration?: number;
  className?: string;
  formatter?: (value: number) => string;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  duration = 1000,
  className = '',
  formatter
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Reset animation when value changes
    setIsAnimating(true);
    setDisplayValue(0);

    // Handle different value types
    let targetValue: number;
    let isRatio = false;
    let ratioParts: { numerator: number; denominator: number } | null = null;

    if (typeof value === 'string') {
      // Check if it's a ratio format like "123/456"
      const ratioMatch = value.match(/^(\d+)\/(\d+)$/);
      if (ratioMatch) {
        isRatio = true;
        ratioParts = {
          numerator: parseInt(ratioMatch[1], 10),
          denominator: parseInt(ratioMatch[2], 10)
        };
        targetValue = ratioParts.numerator;
      } else {
        // Try to parse as number
        const parsed = parseFloat(value.replace(/,/g, ''));
        targetValue = isNaN(parsed) ? 0 : parsed;
      }
    } else {
      targetValue = value;
    }

    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);

      const currentValue = startValue + (targetValue - startValue) * easeOutQuart;

      if (isRatio && ratioParts) {
        // For ratios, we need to calculate the current numerator based on progress
        const currentNumerator = Math.round(currentValue);
        setDisplayValue(currentNumerator);
      } else {
        setDisplayValue(currentValue);
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete
        setIsAnimating(false);
        if (isRatio && ratioParts) {
          setDisplayValue(ratioParts.numerator);
        } else {
          setDisplayValue(targetValue);
        }
      }
    };

    // Start animation
    requestAnimationFrame(animate);

    // Cleanup function
    return () => {
      setIsAnimating(false);
    };
  }, [value, duration]);

  // Format the display value
  const formatDisplayValue = (val: number): string => {
    if (formatter) {
      return formatter(val);
    }

    // Check if original value was a ratio
    if (typeof value === 'string' && value.includes('/')) {
      const ratioMatch = value.match(/^(\d+)\/(\d+)$/);
      if (ratioMatch) {
        const denominator = parseInt(ratioMatch[2], 10);
        return `${Math.round(val)}/${denominator}`;
      }
    }

    // Format as number with commas for thousands
    return Math.round(val).toLocaleString();
  };

  return (
    <span className={`${className} ${isAnimating ? 'animate-pulse' : ''}`}>
      {formatDisplayValue(displayValue)}
    </span>
  );
};

export default AnimatedNumber;
